import { Activity, useEffect, useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  SidePanelLoader,
  PermissionDenied,
  IconBlock,
  IconSelected,
  PermissionEnum,
  Tabs,
  ChangeLogs,
} from '@ui/src';
import { useAppSelector } from '@store';
import Form from '../../../components/accounts/form/form';
import AvailableCurrencies from '../available-currencies';
import useFormFunctions from './hooks/use-form';
import AccountsPopups from '../../../components/accounts/popups/popups';

import cn from './styles.module.css';

const AccountForm = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [closingBalancesAtValue, setClosingBalancesAtValue] = useState('');

  const {
    validators,
    account,
    menuItems,
    balances,
    changeLogs,
    changeLogsPagination,
  } = useAppSelector((state) => state.accounts);
  const { overdraftEnabled: overdraftEnabledSetting } = useAppSelector(
    (state) => state.accountsDictionaries,
  );
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const {
    activePermissions,
    currencies: currenciesList,
    currenciesList: currencies,
    baseCurrency,
    ffSettings,
  } = useAppSelector((state) => state.root);

  const [searchParams] = useSearchParams();

  const { search } = useLocation();
  const { accountId } = useParams();

  const categoryId = searchParams.get('categoryId');
  const groupId = searchParams.get('groupId');
  const primaryId = searchParams.get('primaryId');
  const closingBalancesAt = searchParams.get('closingBalancesAt');

  const {
    control,
    reset,
    errors,
    blockUblockCreditError,
    blockUblockDebitError,
    closeOpenError,
    nextIsDirty,
    description,
    revaluable,
    globalError,
    clientAccountError,
    isLoading,
    updateInProgress,
    creditBlockInProgress,
    creditUnblockInProgress,
    debitBlockInProgress,
    debitUnblockInProgress,
    closeInProgress,
    openInProgress,
    accountLoading,
    isFetching,
    accountBalancesLoading,
    overdraftUpdateInProgress,
    updateOverdraftBalanceIsLoading,
    changeLogsIsLoading,
    changeLogsIsFetching,
    onUpdateOverdraftStatus,
    onBlockCredit,
    onBlockDebit,
    onUpdate,
    onSubmit,
    onClose,
    getChangeLogsAction,
    creditBlock,
    setCreditBlock,
    debitBlock,
    setDebitBlock,
    creditBlockConfirmationVisible,
    setCreditBlockConfirmationVisible,
    debitBlockConfirmationVisible,
    setDebitBlockConfirmationVisible,
    commentPopupVisible,
    setCommentPopupVisible,
    comment,
    setComment,
    availableCurrenciesVisible,
    setAvailableCurrenciesVisible,
    overdraftEnabled,
    accountBalances,
    setAccountBalances,
    defaultBalances,
    activeTab,
    setActiveTab,
    handleClose,
    closingBalancesLoading,
  } = useFormFunctions({
    accountId,
    validators,
    account,
    setPanelIsOpen,
    changeLogs,
    activePermissions,
    search,
    menuItems,
    categoryId,
    groupId,
    primaryId,
    balances,
    overdraftEnabledSetting,
    closingBalancesAt: closingBalancesAtValue,
  });

  useEffect(() => {
    if (closingBalancesAt) {
      setClosingBalancesAtValue(closingBalancesAt);
    }
  }, [closingBalancesAt]);

  let content: React.JSX.Element;

  if (!account && (accountLoading || accountBalancesLoading || isFetching)) {
    content = <SidePanelLoader />;
  } else if (
    !accountId &&
    !activePermissions?.includes(PermissionEnum.ACCOUNTS_CREATE_ACTION)
  ) {
    content = <PermissionDenied />;
  } else if (availableCurrenciesVisible) {
    content = (
      <AvailableCurrencies
        setAvailableCurrenciesVisible={setAvailableCurrenciesVisible}
      />
    );
  } else {
    content = (
      <Form
        accountId={accountId}
        control={control}
        errors={errors}
        balances={accountBalances}
        setBalances={setAccountBalances}
        currencies={currencies}
        baseCurrencyId={baseCurrency?.id}
        totalBalanceInBC={account?.totalBalanceBC}
        clientAccountError={clientAccountError}
        creditBlock={creditBlock}
        debitBlock={debitBlock}
        closingBalancesAt={closingBalancesAtValue}
        closingBalanceAtSearchParam={closingBalancesAt}
        setClosingBalancesAt={setClosingBalancesAtValue}
        setCreditBlock={setCreditBlock}
        setDebitBlock={setDebitBlock}
        setCreditBlockConfirmationVisible={setCreditBlockConfirmationVisible}
        setDebitBlockConfirmationVisible={setDebitBlockConfirmationVisible}
        setAvailableCurrenciesVisible={setAvailableCurrenciesVisible}
        currenciesList={currenciesList}
        overdraftEnabled={overdraftEnabled}
        onUpdateOverdraftStatus={onUpdateOverdraftStatus}
        overdraftUpdateInProgress={overdraftUpdateInProgress}
        overdraftEnabledSetting={overdraftEnabledSetting}
        updateOverdraftBalanceIsLoading={updateOverdraftBalanceIsLoading}
        defaultBalances={defaultBalances}
        closedBalances={
          closingBalancesAtValue ? account?.closingBalances : undefined
        }
        closingBalancesLoading={closingBalancesLoading}
      />
    );
  }

  let blockAccountButtonText;
  let commentPopupButtonText;

  if (openInProgress || closeInProgress) {
    commentPopupButtonText = 'Loading...';
  } else if (account?.closed) {
    commentPopupButtonText = 'Yes, open account';
  } else if (!account?.closed) {
    commentPopupButtonText = 'Yes, close account';
  }

  if (account?.closed) {
    blockAccountButtonText = 'Open account';
  } else if (!account?.closed) {
    blockAccountButtonText = 'Close account';
  }

  let blockButtonText;
  let topupButtonText;

  if (
    creditBlockInProgress ||
    creditUnblockInProgress ||
    debitBlockInProgress ||
    debitUnblockInProgress
  ) {
    blockButtonText = 'Loading...';
    topupButtonText = 'Loading...';
  } else {
    blockButtonText = `Yes, ${!debitBlock ? 'Unblock' : 'Block'}`;
    topupButtonText = `Yes, ${!creditBlock ? 'Unblock' : 'Block'}`;
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        onClose={() => {
          if (nextIsDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
            reset({});
          }
        }}
        errorSidepanel={globalError}
        title={accountId ? 'Account' : 'New Account'}
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="accounts-form"
        footer={
          <div>
            {!accountId &&
              activePermissions?.includes(
                PermissionEnum.ACCOUNTS_CREATE_ACTION,
              ) && (
                <ButtonPrimary
                  text={isLoading ? 'Loading...' : 'Add account'}
                  disabled={!description || isLoading}
                  onClick={onSubmit}
                  bold
                />
              )}
            {accountId && (
              <ButtonPrimary
                text={updateInProgress ? 'Loading...' : 'Save'}
                disabled={
                  (isEqual(
                    {
                      revaluable: account?.revaluable,
                      description: account?.description,
                    },
                    { revaluable, description },
                  ) &&
                    isEqual(defaultBalances, accountBalances)) ||
                  isLoading ||
                  updateInProgress ||
                  updateOverdraftBalanceIsLoading
                }
                onClick={onUpdate}
                bold
              />
            )}
            <ButtonPseudo text="Close" onClick={handleClose} bold />
            {accountId &&
              !isLoading &&
              !isFetching &&
              ((!account?.primary?.isClientAccount &&
                account?.category?.number !== '2') ||
                (account?.category?.number === '2' &&
                  (!account.closed ||
                    (account?.closed &&
                      !ffSettings?.ffAccountReopenDisabled))) ||
                (account?.primary?.isClientAccount &&
                  (!account?.closed ||
                    (account?.closed &&
                      !ffSettings?.ffAccountReopenDisabled)))) && (
                <ButtonPseudo
                  text={blockAccountButtonText}
                  icon={
                    !account?.closed ? (
                      <IconBlock className={cn.blockIcon} />
                    ) : (
                      <IconSelected className={cn.selectedIcon} />
                    )
                  }
                  className={`${cn.blockButton} ${
                    !account?.closed ? cn.redButton : ''
                  }`}
                  onClick={() => setCommentPopupVisible(true)}
                  disabled={closeInProgress}
                  bold
                />
              )}
          </div>
        }
        closeOnClickOutside
      >
        <>
          {accountId && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'details',
                },
                {
                  name: 'Change log',
                  value: 'logs',
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'details' | 'logs')}
            />
          )}
          <Activity mode={activeTab === 'details' ? 'visible' : 'hidden'}>
            {content}
          </Activity>
          <Activity mode={activeTab === 'logs' ? 'visible' : 'hidden'}>
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="ACCOUNT"
                  entityId={accountId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          </Activity>
        </>
      </SidePanel>
      <AccountsPopups
        setDirtyPopupVisible={setDirtyPopupVisible}
        handleClose={handleClose}
        dirtyPopupVisible={dirtyPopupVisible}
        creditBlock={creditBlock}
        setCreditBlockConfirmationVisible={setCreditBlockConfirmationVisible}
        setCreditBlock={setCreditBlock}
        onBlockCredit={onBlockCredit}
        topupButtonText={topupButtonText}
        creditBlockConfirmationVisible={creditBlockConfirmationVisible}
        blockUblockCreditError={blockUblockCreditError}
        account={account}
        setComment={setComment}
        setCommentPopupVisible={setCommentPopupVisible}
        commentPopupVisible={commentPopupVisible}
        comment={comment}
        closeOpenError={closeOpenError}
        commentPopupButtonText={commentPopupButtonText || ''}
        onClose={onClose}
        openInProgress={openInProgress}
        closeInProgress={closeInProgress}
        debitBlock={debitBlock}
        setDebitBlockConfirmationVisible={setDebitBlockConfirmationVisible}
        setDebitBlock={setDebitBlock}
        onBlockDebit={onBlockDebit}
        blockButtonText={blockButtonText}
        debitBlockConfirmationVisible={debitBlockConfirmationVisible}
        blockUblockDebitError={blockUblockDebitError}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default AccountForm;
